import { Link, useLocation } from '@tanstack/react-router';
import { Bot, Cpu, Download, FlaskConical, Plane, Settings, Wrench } from 'lucide-react';
import type { ComponentType } from 'react';
import { isTauri } from '@/lib/tauri';
import { resolveDownload } from '@/lib/downloadTarget';
import { log } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { useUpdater } from '@/store/updater';

interface RailItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

/**
 * Top-level workspaces shown in the icon rail: Pilot (the live-flight
 * surface), Assets (real-drone setup + health), SITL (simulated drones)
 * and Diva (LLM assistant).
 */
const ITEMS: RailItem[] = [
  { to: '/', label: 'Pilot', icon: Plane },
  { to: '/analyse', label: 'Analyse', icon: FlaskConical },
  { to: '/assets', label: 'Assets', icon: Wrench },
  { to: '/sitl', label: 'SITL', icon: Cpu },
  { to: '/assistant', label: 'Diva', icon: Bot },
];

/**
 * Left-edge icon rail. Active workspace gets a cyan left bar + brighter icon.
 */
export function IconRail() {
  const showDownload = !isTauri();
  return (
    <nav
      className="flex flex-none flex-col border-r border-border-default bg-surface-1"
      style={{ width: 'var(--rail-iconrail)' }}
    >
      {/* No overflow-* here: overflow-y-auto would force overflow-x to auto,
          and the off-edge hover tooltips then breed a horizontal scrollbar. */}
      <div className="flex-1 py-1">
        {ITEMS.map((it) => (
          <RailButton key={it.to} item={it} />
        ))}
      </div>
      <div className="border-t border-border-default py-1.5">
        <RailButton item={{ to: '/settings', label: 'Settings', icon: Settings }} />
        {showDownload && <DownloadRailButton />}
        {!showDownload && <UpdateRailButton />}
      </div>
    </nav>
  );
}

function DownloadRailButton() {
  const target = resolveDownload();
  const label = `Download for ${target.label}`;
  function onClick() {
    log.action('Desktop download clicked', { os: target.os, url: target.url });
  }
  return (
    <a
      href={target.url}
      onClick={onClick}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      className="group relative flex h-9 w-full items-center justify-center text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg-secondary"
    >
      <Download className="h-[18px] w-[18px]" />
      <span
        className="pointer-events-none invisible absolute left-full ml-1 z-50 whitespace-nowrap rounded border border-border-default bg-surface-2 px-2 py-1 text-[11px] font-medium text-fg-primary opacity-0 shadow-[var(--shadow-pop)] transition-opacity group-hover:visible group-hover:opacity-100"
      >
        {label}
      </span>
    </a>
  );
}

/**
 * In-Tauri counterpart of `DownloadRailButton`. Shows the same Download
 * glyph but with an accent dot when an update is pending, and clicking it
 * opens the UpdateDialog (desktop) or kicks the APK browser-open (Android).
 * When no update is pending, the button is hidden — the user already has
 * the latest, no need to clutter the rail.
 */
function UpdateRailButton() {
  const update = useUpdater((s) => s.update);
  const openDialog = useUpdater((s) => s.openDialog);
  if (!update) return null;
  const label = `Update available — Console ${update.version}`;
  return (
    <button
      type="button"
      onClick={openDialog}
      title={label}
      className="group relative flex h-9 w-full items-center justify-center text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg-secondary"
    >
      <Download className="h-[18px] w-[18px]" />
      <span
        aria-hidden
        className="absolute top-2 right-3 h-2 w-2 rounded-full"
        style={{ background: 'var(--color-accent)' }}
      />
      <span
        className="pointer-events-none invisible absolute left-full ml-1 z-50 whitespace-nowrap rounded border border-border-default bg-surface-2 px-2 py-1 text-[11px] font-medium text-fg-primary opacity-0 shadow-[var(--shadow-pop)] transition-opacity group-hover:visible group-hover:opacity-100"
      >
        {label}
      </span>
    </button>
  );
}

function RailButton({ item }: { item: RailItem }) {
  const loc = useLocation();
  const active = loc.pathname === item.to;
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      title={item.label}
      className={cn(
        'group relative flex h-9 w-full items-center justify-center transition-colors',
        active ? 'text-fg-primary' : 'text-fg-muted hover:bg-surface-2 hover:text-fg-secondary',
      )}
    >
      {active && (
        <span
          className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full"
          style={{ background: 'var(--color-accent)' }}
        />
      )}
      <Icon className="h-[17px] w-[17px]" />
      <span
        className="pointer-events-none invisible absolute left-full ml-1 z-50 rounded border border-border-default bg-surface-2 px-2 py-1 text-[11px] font-medium text-fg-primary opacity-0 shadow-[var(--shadow-pop)] transition-opacity group-hover:visible group-hover:opacity-100"
      >
        {item.label}
      </span>
    </Link>
  );
}
